import { useState, useEffect, useRef, useCallback } from 'react';

export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface ParsedAddress {
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface UseGooglePlacesReturn {
  isLoaded: boolean;
  loadError: string | null;
  predictions: PlacePrediction[];
  isLoadingPredictions: boolean;
  fetchPredictions: (input: string) => void;
  getPlaceDetails: (placeId: string) => Promise<ParsedAddress | null>;
  clearPredictions: () => void;
}

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

// Module-level script loading — shared across all hook instances
let scriptLoadPromise: Promise<void> | null = null;

function loadGoogleMapsScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  if (window.google?.maps?.places) {
    scriptLoadPromise = Promise.resolve();
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function parseAddressComponents(
  components: google.maps.places.AddressComponent[]
): ParsedAddress {
  let streetNumber = '';
  let route = '';
  let city = '';
  let state = '';
  let zipCode = '';

  for (const component of components) {
    const types = component.types;
    if (types.includes('street_number')) {
      streetNumber = component.longText || '';
    } else if (types.includes('route')) {
      route = component.longText || '';
    } else if (types.includes('locality')) {
      city = component.longText || '';
    } else if (types.includes('sublocality_level_1') && !city) {
      city = component.longText || '';
    } else if (types.includes('administrative_area_level_1')) {
      state = component.shortText || '';
    } else if (types.includes('postal_code')) {
      zipCode = component.longText || '';
    }
  }

  const streetAddress = streetNumber
    ? `${streetNumber} ${route}`
    : route;

  return {
    street_address: streetAddress,
    city,
    state,
    zip_code: zipCode,
  };
}

export function useGooglePlaces(): UseGooglePlacesReturn {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    API_KEY ? null : 'No API key configured'
  );
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);

  useEffect(() => {
    if (!API_KEY) return;

    let cancelled = false;

    loadGoogleMapsScript()
      .then(() => {
        if (cancelled) return;
        setIsLoaded(true);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load Google Maps');
      });

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!isLoaded || input.length < 3) {
      setPredictions([]);
      setIsLoadingPredictions(false);
      return;
    }

    setIsLoadingPredictions(true);
    const generation = ++generationRef.current;

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const { suggestions } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: ['us'],
            includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
          });

        if (generation !== generationRef.current) return;

        setIsLoadingPredictions(false);
        setPredictions(
          suggestions
            .filter((s) => s.placePrediction)
            .map((s) => {
              const p = s.placePrediction!;
              return {
                placeId: p.placeId,
                description: p.text?.text || '',
                mainText: p.mainText?.text || '',
                secondaryText: p.secondaryText?.text || '',
              };
            })
        );
      } catch (err) {
        if (generation !== generationRef.current) return;
        console.warn('[useGooglePlaces] fetchAutocompleteSuggestions error:', err);
        setIsLoadingPredictions(false);
        setPredictions([]);
      }
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<ParsedAddress | null> => {
      try {
        const place = new google.maps.places.Place({ id: placeId });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('fetchFields timed out after 5s')), 5000)
        );

        const result = await Promise.race([
          place.fetchFields({ fields: ['addressComponents', 'location'] }),
          timeoutPromise,
        ]);

        const fetched = (result as any)?.place || place;
        const components = fetched.addressComponents || place.addressComponents;
        if (components && components.length > 0) {
          const parsed = parseAddressComponents(components);
          // place.location is a google.maps.LatLng (lat()/lng() methods); tolerate
          // a plain {lat,lng} literal too in case the SDK shape changes.
          const loc = fetched.location || place.location;
          if (loc) {
            const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
            const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
            if (typeof lat === 'number' && typeof lng === 'number') {
              parsed.latitude = lat;
              parsed.longitude = lng;
            }
          }
          return parsed;
        }
        return null;
      } catch (err) {
        console.warn('[useGooglePlaces] fetchFields error:', err);
        return null;
      }
    },
    []
  );

  return {
    isLoaded,
    loadError,
    predictions,
    isLoadingPredictions,
    fetchPredictions,
    getPlaceDetails,
    clearPredictions,
  };
}
